import { useState, type DragEvent } from "react";
import { useAngemeldet } from "../../kern/sitzung";
import { personNach, type PersonKurz } from "../../kern/personen";
import { Haken } from "../../kern/ui/Haken";
import { PersonenAuswahl } from "../../kern/ui/PersonenAuswahl";
import { Stapel } from "../../kern/ui/Stapel";
import { Statuswahl } from "../../kern/ui/Statuswahl";
import { hakenGesperrt, PRIORITAET, STATUS, STATUS_OPTIONEN, type Aufgabe, type Status } from "./logik";
import { sortierenImSprint } from "./sprintlogik";
import { useSchnellaktionen } from "./aktionen";
import { useKapsel } from "./vorhaben";
import { waehlbareIds } from "./vorhabenlogik";
import { AufgabeLink } from "../../kern/ui/AufgabeLink";
import ui from "../../kern/ui/ui.module.css";
import s from "./sprint.module.css";

type Schnell = ReturnType<typeof useSchnellaktionen>;

interface Gemeinsam {
  personen: readonly PersonKurz[] | undefined;
  ressorts: readonly { id: string; name: string }[] | undefined;
  sa: Schnell;
}

const STATUS_LISTE = Object.keys(STATUS) as Status[];

/** Verantwortliche direkt in der Zeile: Knopf mit dem Stapel, er öffnet dieselbe Auswahl wie im Dialog (Stufe 2).
 *  Vorstand wählt alle Aktiven, die Leitung eines Vorhabens die Personen daraus, sonst nur sich selbst. Ohne Verantwortliche steht die
 *  Aufgabe unter Offen. */
export function PersonWahl({ a, personen, sa }: { a: Aufgabe } & Pick<Gemeinsam, "personen" | "sa">) {
  const { person: ich, rechte } = useAngemeldet();
  const kapsel = useKapsel();
  const vorstand = rechte.has("aufgaben.alle_bearbeiten");
  const leitet = kapsel.leitet(a.vorhaben_id);
  const darf = vorstand || leitet || a.personen.includes(ich.id) || a.erstellt_von === ich.id;
  const aktiv = (personen ?? []).filter((p) => p.aktiv);
  const erlaubt = new Set(
    waehlbareIds({ vorstand, ich: ich.id, aktive: aktiv.map((p) => p.id), leitet, sichtbarkeit: a.sichtbarkeit, imVorhaben: kapsel.personenVon(a.vorhaben_id) }),
  );
  return (
    <PersonenAuswahl
      art="stapel"
      label={`Verantwortlich für „${a.titel}“`}
      ausgewaehlt={a.personen}
      personen={personen}
      waehlbar={!darf ? [] : aktiv.filter((p) => erlaubt.has(p.id))}
      entfernbar={(pid) => darf && (vorstand || erlaubt.has(pid))}
      gesperrt={!darf || a.sprint_status === "abgeschlossen"}
      onHinzu={(pid) => sa.personHinzu(a, pid)}
      onWeg={(pid) => sa.akt.personWeg.mutate({ id: a.id, person: pid }, { onError: sa.fehler })}
    />
  );
}

interface BoardProps extends Gemeinsam {
  liste: readonly Aufgabe[];
  unterVon: (id: string) => readonly Aufgabe[];
  /** Auf der Seite eines Vorhabens: ohne Etikett des Vorhabens */
  ohneVorhaben?: boolean;
}

/** Board mit vier Spalten nach Status. Karten ziehen, dazu die Status-Auswahl in der Karte für Tastatur und Handy. */
export function SprintBoard({ liste, unterVon, personen, ressorts, sa, ohneVorhaben }: BoardProps) {
  const { person: ich, rechte } = useAngemeldet();
  const kapsel = useKapsel();
  const alleBearbeiten = rechte.has("aufgaben.alle_bearbeiten");
  const [ziel, setZiel] = useState<Status | null>(null);

  function ablegen(e: DragEvent, status: Status) {
    e.preventDefault();
    setZiel(null);
    const a = liste.find((x) => x.id === e.dataTransfer.getData("text/plain"));
    if (a && a.status !== status) sa.statusSetzen(a, status);
  }

  return (
    <>
      <div className={s.board}>
        {STATUS_LISTE.map((st) => {
          const karten = sortierenImSprint(liste.filter((a) => a.status === st));
          return (
            <section
              key={st}
              className={[ui.karte, s.spalte].join(" ")}
              aria-label={STATUS[st]}
              data-ziel={ziel === st}
              onDragOver={(e) => {
                e.preventDefault();
                setZiel(st);
              }}
              onDragLeave={() => setZiel((z) => (z === st ? null : z))}
              onDrop={(e) => ablegen(e, st)}
            >
              <h3>
                <span>{STATUS[st]}</span>
                <span className={s.meta}>{karten.length}</span>
              </h3>
              {karten.length === 0 ? <p className={s.meta}>Karte hierher ziehen</p> : null}
              {karten.map((a) => {
                const verantwortlich = a.personen.map((id) => personNach(personen, id));
                const unter = unterVon(a.id);
                const ressort = ressorts?.find((r) => r.id === a.ressort_id)?.name;
                const vorhaben = ohneVorhaben ? null : kapsel.name(a.vorhaben_id);
                return (
                  <div
                    key={a.id}
                    className={[ui.innen, s.karte].join(" ")}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData("text/plain", a.id);
                      e.dataTransfer.effectAllowed = "move";
                    }}
                  >
                    <div className={s.kartenKopf}>
                      <Haken
                        an={a.status === "erledigt"}
                        titel={a.titel}
                        gesperrt={hakenGesperrt(a, ich.id, alleBearbeiten, kapsel.leitet(a.vorhaben_id))}
                        onUmschalten={() => sa.erledigtUmschalten(a)}
                      />
                      <AufgabeLink id={a.id} className={s.titel}>
                        {a.titel}
                      </AufgabeLink>
                    </div>
                    <div className={s.kartenMeta}>
                      <span className={[s.marke, s[`p-${a.prioritaet}`]].filter(Boolean).join(" ")}>Prio {PRIORITAET[a.prioritaet].toLowerCase()}</span>
                      {ressort ? <span className={s.marke}>{ressort}</span> : null}
                      {vorhaben ? (
                        <span className={[s.marke, s.vorhabenMarke].join(" ")}>
                          <span className="nur-leser">Vorhaben </span>
                          {vorhaben}
                        </span>
                      ) : null}
                    </div>
                    <div className={s.kartenFuss}>
                      <span className={s.wer}>
                        <Stapel personen={verantwortlich} />
                        {verantwortlich.length === 0 ? "Offen" : verantwortlich.length === 1 ? (verantwortlich[0]?.vorname ?? "") : null}
                      </span>
                      {unter.length ? (
                        <span className={s.meta}>
                          {unter.filter((u) => u.status === "erledigt").length} von {unter.length} Unteraufgaben
                        </span>
                      ) : null}
                    </div>
                    <span className={s.kartenStatus}>
                      <Statuswahl name={a.titel} optionen={STATUS_OPTIONEN} wert={a.status} darf breite={136} onWechsel={(neu) => sa.statusSetzen(a, neu)} />
                    </span>
                  </div>
                );
              })}
            </section>
          );
        })}
      </div>
      <p className={s.meta}>Karten ziehen oder den Status in der Karte wählen.</p>
    </>
  );
}
